import { useEffect, useRef, useState } from 'react'
import { isUsableUrlTitle, urlDisplayTitle, type ContentType, type UrlEntity } from '../domain/note'
import { deriveLinkMetadataStatus, hasExhaustedLinkMetadataRetries, hasSpecificLinkImage, linkDetailsLoading, linkPreviewImageUrl, type LinkMetadataStatus } from '../links/linkMetadata'
import { linkPreviewPresentation } from '../links/linkPreviewCategory'
import { hideMarkdownPreviewArtifacts } from '../utils/markdownText'
import { GIST_SETTINGS } from '../links/gistPolicy'
import type { LinkGistUi } from '../links/gistController'
import { blockedMessage, linkSummary } from '../links/linkSummary'
import { GistLine } from './GistLine'
import { AnswerLine } from './LinkAnswers'
import { Icon } from './Icon'
import { LinkActionsMenu, copyLinkAddress } from './LinkActionsMenu'
import { buildLinkMenu, linkHost } from '../links/linkActions'
import type { LinkDisplayChoice } from '../links/linkDisplay'
import { formatDuration } from './LinkPreview'
import { CardKindLine } from './BrowseCardContent'
import { linkTone } from './cardKind'

interface LinkNoteCardProps {
  url: UrlEntity
  contentType: ContentType
  /** The label the person wrote in the note, which wins over fetched metadata. */
  fallbackTitle?: string
  metadataStatus?: LinkMetadataStatus | null
  onRetryMetadata?: (url: UrlEntity) => void
  /** Offered in the ⋯ menu when given: edit the link's label or address. */
  onEdit?: (url: UrlEntity) => void
  /** Offered in the ⋯ menu when given: remove the link, keep its words. */
  onRemove?: (url: UrlEntity) => void
  /** Offered in the ⋯ menu when given: remove the link and its words. */
  onRemoveLink?: (url: UrlEntity) => void
  /** Present when this build shows gists. */
  gist?: LinkGistUi
  /** The shared display switch (Pill, Row, Preview); this card is the Preview. */
  onDisplay?: (choice: LinkDisplayChoice) => void
  /** Opens the full-screen view: on the summary, on an answer (its id), or on the question field (`ask`). */
  onExpand?: (selected: string) => void
  /** `now` when the card was opened on purpose (the preview sheet): the page may be read at once rather than after a dwell. */
  attention?: 'dwell' | 'now'
  /** Merged with the note's unpacked screenshot (links/mergeUnpacked.ts): the card joins it as one, wearing the reading's kind. */
  merged?: { kindLabel: string }
}

/**
 * The link as the note itself: shown above the editor when a note's first block is its only link, in
 * place of both the text link and a dock/rail attachment. Opening is the largest target; the rest of
 * the note stays writable below it.
 */
export function LinkNoteCard({ url, contentType, fallbackTitle, metadataStatus, onRetryMetadata, onEdit, onRemove, onRemoveLink, gist, onDisplay, onExpand, attention = 'dwell', merged }: LinkNoteCardProps) {
  const basePresentation = linkPreviewPresentation(url, contentType)
  const presentation = merged ? { ...basePresentation, label: merged.kindLabel } : basePresentation
  const status = metadataStatus === undefined ? deriveLinkMetadataStatus(url) : metadataStatus
  const pending = linkDetailsLoading(url, status)
  const title = hideMarkdownPreviewArtifacts(
    isUsableUrlTitle(url, url.title) ? urlDisplayTitle(url) : fallbackTitle?.trim() || urlDisplayTitle(url),
  )
  const image = linkPreviewImageUrl(url.imageUrl)
  const [failedImage, setFailedImage] = useState<string | null>(null)
  const showImage = Boolean(image && image !== failedImage && hasSpecificLinkImage(url, url.imageUrl))
  const [menuOpen, setMenuOpen] = useState(false)
  const menuButton = useRef<HTMLButtonElement>(null)
  const meta = [
    presentation.provider === title ? null : presentation.provider,
    url.author,
    typeof url.publishedAt === 'number' ? new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric' }).format(url.publishedAt) : null,
  ].filter((value): value is string => Boolean(value))
  const duration = presentation.category === 'video' && typeof url.durationSeconds === 'number' ? formatDuration(url.durationSeconds) : null

  // A card that stays on screen for a moment counts as attention: that is when its page may be read.
  const wantsGist = Boolean(gist && !url.gist && url.fetchedAt !== null)
  const want = gist?.want
  useEffect(() => {
    if (!wantsGist || !want) return
    // Opened on purpose: the person asked, so it is read at once and a failure is explained. Otherwise attention is a dwell.
    const timer = window.setTimeout(() => (attention === 'now' ? want('manual', true) : want('card-visible')), attention === 'now' ? 0 : GIST_SETTINGS.visibleDwellMs)
    return () => window.clearTimeout(timer)
  }, [wantsGist, want, url.originalUrl, attention])
  const model = buildLinkMenu({
    open: { href: url.originalUrl, label: presentation.actionLabel, host: linkHost(url.originalUrl) },
    onCopy: () => copyLinkAddress(url.originalUrl),
    onRetry: onRetryMetadata && hasExhaustedLinkMetadataRetries(url) ? () => onRetryMetadata(url) : undefined,
    summary: gist ? { state: url.gist ? (url.gist.hidden ? 'hidden' : 'shown') : 'none', inPlace: true, unavailable: Boolean(gist.blockedReason), run: () => gist.want('manual', true) } : undefined,
    ask: gist && onExpand ? { unavailable: Boolean(gist.blockedReason), run: () => onExpand('ask') } : undefined,
    onEdit: onEdit ? () => onEdit(url) : undefined,
    onRemove: onRemove ? () => onRemove(url) : undefined,
    onRemoveLink: onRemoveLink ? () => onRemoveLink(url) : undefined,
    display: onDisplay ? { value: 'preview', onChange: onDisplay } : undefined,
  })

  // A video's length sits on its picture, or with the facts when it has none.
  const facts = !showImage && duration ? [...meta, duration] : meta
  // A picture is the card's art or there is none: no placeholder box (DESIGN.md, "Browse card"); the kind line says what it is.
  return <article className="link-note-card" data-link-category={presentation.category} data-link-pending={pending || undefined} data-merged={merged ? '' : undefined}
    data-art={showImage ? undefined : 'none'} aria-label={`${presentation.label}: ${title}`}>
    {showImage ? (
      <a className="link-note-card__art" href={url.originalUrl} target="_blank" rel="noreferrer noopener" tabIndex={-1} aria-hidden="true">
        <img src={image!} alt="" crossOrigin="anonymous" referrerPolicy="no-referrer" loading="lazy" decoding="async" onError={() => setFailedImage(image)} />
        {duration ? <span className="link-note-card__duration">{duration}</span> : null}
      </a>
    ) : null}
    <div className="link-note-card__body">
      <CardKindLine kind={{ icon: presentation.icon, tone: linkTone(presentation.category), word: presentation.label, source: null, when: null }} />
      <h2 className={pending ? 'link-note-card__title t-shimmer' : 'link-note-card__title'} data-text={pending ? title : undefined}>{title}</h2>
      {facts.length ? <p className="link-note-card__meta">{facts.map((item) => <span key={item}>{item}</span>)}</p> : null}
      {gist ? <GistLine summary={linkSummary(url, gist.status, gist.statusDetail)} onRefresh={() => gist.want('manual', true)} onHide={gist.hide} onExpand={onExpand ? () => onExpand('summary') : undefined} /> : null}
      {gist?.blockedReason && !url.gist ? <div className="gist-line gist-line--note" role="status">
        <div className="gist-line__row"><Icon name="help" /><p>{blockedMessage(gist.blockedReason)}</p></div>
      </div> : null}
      {gist && url.answers?.[0] ? <AnswerLine answer={url.answers[0]} onExpand={onExpand ? () => onExpand(url.answers![0]!.id) : undefined} /> : null}
    </div>
    <div className="link-note-card__actions">
      <a className="btn btn-primary link-note-card__open" href={url.originalUrl} target="_blank" rel="noreferrer noopener">
        <Icon name="external" />{presentation.actionLabel}
      </a>
      <button type="button" ref={menuButton} className="btn btn-ghost btn-square link-note-card__more" aria-label="More link actions" aria-haspopup="menu" aria-expanded={menuOpen}
        onClick={() => setMenuOpen((open) => !open)}>
        <Icon name="more" />
      </button>
    </div>
    {menuOpen ? <LinkActionsMenu model={model} title={title} meta={[presentation.label, presentation.provider].filter(Boolean).join(' · ')} anchor={menuButton.current} onClose={() => setMenuOpen(false)} /> : null}
  </article>
}
